import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import {
  ApprovalResolvingStatus,
  IApprovalSummary,
  IBulkApprovalResult,
} from '@common/interfaces/atlasai'
import { bulkResultFrom } from '../../../redux/atlasai/bulkApprovals'
import Button from '../../Common/Button/Button'
import Icon from '../../Common/Icon'
import IconButton from '../../Common/IconButton'
import { SnackbarType } from '../../Common/Snackbar'
import Text from '../../Common/Text'
import ApprovalCard from './ApprovalCard'
import ApprovalSelectionList from './ApprovalSelectionList'
import styles from './PendingApprovals.module.scss'
import { PropsFromRedux } from './index'

export type IProps = PropsFromRedux & {
  /** Chat-session scope — a conversation or a message thread. */
  sessionId?: string | null
  /**
   * Task scope, for the Task detail panel. A task-bound Arc session has no
   * readable surface of its own, so the task — not its hidden session — is what
   * the panel knows and what the approval is filed under. Mutually exclusive
   * with `sessionId` in practice; passing both narrows to their intersection.
   */
  taskId?: string | null
  /** Bumps whenever a new assistant turn completes — triggers a refetch. */
  refreshKey?: number
  /**
   * Fired after an approve/reject lands. Surfaces that write to the host, which
   * on the Task page is the only thing that reloads its comments — resolving an
   * approval posts an outcome comment server-side, and tasks have no live push
   * for one (channels get CHANNEL_MESSAGE_CREATED; tasks have no equivalent).
   */
  onResolved?: () => void
  /** Only approvals raised at or after this time (epoch ms), e.g. since a call began. */
  createdSince?: number
  /** Leave out this session's cards, when they already show somewhere else on screen. */
  excludeSessionId?: string | null
}

/** Which bulk action ran, so the outcome can name what happened. */
type BulkMode = 'approve' | 'reject'

const BULK_VERB: Record<BulkMode, string> = { approve: 'approved', reject: 'rejected' }

/**
 * The batch outcome as the app's operation-result snackbar, which is where it
 * has to go: a clean run empties the list, so the panel that would otherwise
 * report it is gone by the time there is anything to say. Which cards failed is
 * still shown inline against the rows themselves.
 */
const bulkNotification = (mode: BulkMode, result: IBulkApprovalResult) => {
  const succeeded = result.succeededIds.length
  const failed = result.failures.length
  const verb = BULK_VERB[mode]
  if (failed === 0) return { message: `${succeeded} ${verb}`, type: SnackbarType.Success }
  if (succeeded === 0) {
    return { message: `Nothing was ${verb} — all ${failed} failed`, type: SnackbarType.Error }
  }
  return { message: `${succeeded} ${verb}, ${failed} failed`, type: SnackbarType.Warning }
}

const PendingApprovals = ({
  sessionId,
  taskId,
  refreshKey,
  onResolved,
  createdSince,
  excludeSessionId,
  pending,
  resolving,
  listApprovals,
  approveApproval,
  rejectApproval,
  approveApprovals,
  rejectApprovals,
  putNotification,
}: IProps) => {
  /**
   * Only this mount's cards.
   *
   * The redux list spans every scope — APPROVAL_CREATED prepends whatever the
   * server pushes, whichever run raised it — so a mount that rendered it raw
   * showed cards belonging to somewhere else. Harmless while the Arc chat was the
   * only mount; not once a message thread and a Task panel each mount their own.
   *
   * An unscoped mount (neither prop) still shows everything, which is what the
   * floating chat wants.
   */
  const approvals = useMemo(
    () =>
      pending.filter(
        (approval) =>
          (!sessionId || approval.sessionId === sessionId) &&
          (!taskId || approval.taskId === taskId) &&
          (!excludeSessionId || approval.sessionId !== excludeSessionId) &&
          (createdSince === undefined || Date.parse(approval.createdAt) >= createdSince),
      ),
    [pending, sessionId, taskId, createdSince, excludeSessionId],
  )

  // Index of the visible card. Cards are newest-first (List returns DESC and
  // APPROVAL_CREATED prepends), so index 0 is the most recent.
  const [currentIndex, setCurrentIndex] = useState(0)
  const prevCount = useRef(approvals.length)

  // Multi-select state. `isSelecting` swaps the single-card view for the
  // tickable list; the card carousel is untouched while it is off.
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkMode, setBulkMode] = useState<BulkMode | null>(null)
  // Why each card the last bulk could not resolve failed, keyed by approval id.
  const [failuresById, setFailuresById] = useState<Record<string, string>>({})
  // The same failures as whole rows, captured before the batch ran — see `retiredFailures`.
  const [failedApprovals, setFailedApprovals] = useState<IApprovalSummary[]>([])

  useEffect(() => {
    if (sessionId) {
      void listApprovals({ sessionId })
      return
    }
    if (taskId) void listApprovals({ taskId })
  }, [sessionId, taskId, refreshKey, listApprovals])

  // When a new approval arrives, jump to it (the newest, index 0). When the
  // visible card resolves and the list shrinks, clamp into range.
  useEffect(() => {
    if (approvals.length > prevCount.current) {
      setCurrentIndex(0)
    } else if (currentIndex > approvals.length - 1) {
      setCurrentIndex(Math.max(0, approvals.length - 1))
    }
    prevCount.current = approvals.length
  }, [approvals.length, currentIndex])

  // Awaited, unlike the fire-and-forget these used to be: the host refresh has
  // to run after the write, not alongside it, or it refetches the comments the
  // outcome note has not been added to yet.
  const handleApprove = useCallback(
    async (approvalId: string, paramOverrides?: Record<string, unknown>) => {
      await approveApproval(approvalId, paramOverrides)
      onResolved?.()
    },
    [approveApproval, onResolved],
  )

  const handleReject = useCallback(
    async (approvalId: string) => {
      await rejectApproval(approvalId)
      onResolved?.()
    },
    [rejectApproval, onResolved],
  )

  const handlePrev = useCallback(() => setCurrentIndex((i) => Math.max(0, i - 1)), [])
  const handleNext = useCallback(
    () => setCurrentIndex((i) => Math.min(approvals.length - 1, i + 1)),
    [approvals.length],
  )

  /**
   * The selection, minus anything that has left the list.
   *
   * Cards come and go under this component — another tab resolves one, the
   * server pushes a new one mid-selection — so a raw `selectedIds` can hold ids
   * that are no longer on screen. Intersect before counting or acting, or the
   * count would claim rows the user cannot see and the bulk call would try to
   * resolve them.
   */
  const effectiveSelection = useMemo(
    () => approvals.map((approval) => approval.id).filter((id) => selectedIds.has(id)),
    [approvals, selectedIds],
  )

  /**
   * Failed rows of the last batch that have since left the list. The server retires
   * a failed approve (APPROVAL_RESOLVED), usually before the batch even returns, so
   * without these its reason would vanish along with the row.
   */
  const retiredFailures = useMemo(() => {
    const liveIds = new Set(approvals.map((approval) => approval.id))
    return failedApprovals.filter((approval) => !liveIds.has(approval.id))
  }, [approvals, failedApprovals])

  const handleToggleOne = useCallback((approvalId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(approvalId)) next.delete(approvalId)
      else next.add(approvalId)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = approvals.length > 0 && approvals.every((a) => prev.has(a.id))
      return allSelected ? new Set() : new Set(approvals.map((a) => a.id))
    })
  }, [approvals])

  const enterSelection = useCallback(() => setIsSelecting(true), [])

  const exitSelection = useCallback(() => {
    setIsSelecting(false)
    setSelectedIds(new Set())
    setFailuresById({})
    setFailedApprovals([])
  }, [])

  // Nothing left to tick or read — drop back to the card view so a card arriving
  // later doesn't land the user inside a selection they had already finished with.
  // Never mid-batch: each card's APPROVAL_RESOLVED can empty the list before the
  // batch returns the failures that keep their rows on screen.
  const isEmpty = approvals.length === 0 && retiredFailures.length === 0
  const isBulkResolving = bulkMode !== null
  useEffect(() => {
    if (isEmpty && !isBulkResolving) exitSelection()
  }, [isEmpty, isBulkResolving, exitSelection])

  const handleBulkResolve = useCallback(
    async (mode: BulkMode) => {
      if (effectiveSelection.length === 0 || bulkMode) return
      setBulkMode(mode)
      setFailuresById({})
      setFailedApprovals([])
      const batch = approvals.filter((approval) => effectiveSelection.includes(approval.id))
      try {
        const raw = await (mode === 'approve'
          ? approveApprovals(effectiveSelection)
          : rejectApprovals(effectiveSelection))
        const result = bulkResultFrom(raw) ?? {
          succeededIds: [],
          failures: effectiveSelection.map((approvalId) => ({
            approvalId,
            message: 'Request failed — nothing was resolved.',
          })),
        }
        const failedIds = new Set(result.failures.map((failure) => failure.approvalId))
        setFailuresById(
          Object.fromEntries(
            result.failures.map(({ approvalId, message }) => [approvalId, message]),
          ),
        )
        setFailedApprovals(batch.filter((approval) => failedIds.has(approval.id)))
        // Whatever failed stays ticked, so the retry re-runs exactly those still listed.
        setSelectedIds(failedIds)
        putNotification(bulkNotification(mode, result))
        onResolved?.()
      } finally {
        setBulkMode(null)
      }
    },
    [
      approvals,
      effectiveSelection,
      bulkMode,
      approveApprovals,
      rejectApprovals,
      putNotification,
      onResolved,
    ],
  )

  const handleBulkApprove = useCallback((): void => {
    void handleBulkResolve('approve')
  }, [handleBulkResolve])

  const handleBulkReject = useCallback((): void => {
    void handleBulkResolve('reject')
  }, [handleBulkResolve])

  if (isEmpty && !isBulkResolving) return null

  const safeIndex = Math.max(0, Math.min(currentIndex, approvals.length - 1))
  // Undefined only while the selection is left holding nothing but retired failures.
  const approval: IApprovalSummary | undefined = approvals[safeIndex]
  const resolvingState = approval ? resolving[approval.id] : undefined
  const hasMultiple = approvals.length > 1
  const selectedCount = effectiveSelection.length

  return (
    <Box className={styles.root}>
      {isSelecting ? (
        <Box className={styles.pager}>
          <Text variant="bodySm" tone="secondary" strong component="span">
            {`${selectedCount} of ${approvals.length} selected`}
          </Text>
          <Button small secondary disabled={isBulkResolving} onClick={exitSelection}>
            Cancel
          </Button>
        </Box>
      ) : (
        hasMultiple && (
          <Box className={styles.pager}>
            <Box className={styles.pagerNav}>
              <IconButton
                title="Newer approval"
                size="small"
                disabled={safeIndex === 0}
                onClick={handlePrev}
              >
                <Icon name="chevron-left" size="sm" />
              </IconButton>
              <Text variant="bodySm" tone="secondary" strong component="span">
                {`Approval ${safeIndex + 1} of ${approvals.length}`}
              </Text>
              <IconButton
                title="Older approval"
                size="small"
                disabled={safeIndex === approvals.length - 1}
                onClick={handleNext}
              >
                <Icon name="chevron-right" size="sm" />
              </IconButton>
            </Box>
            <Button small secondary onClick={enterSelection}>
              Select
            </Button>
          </Box>
        )
      )}

      {isSelecting && (
        <>
          <ApprovalSelectionList
            approvals={approvals}
            retiredApprovals={retiredFailures}
            selectedIds={selectedIds}
            failuresById={failuresById}
            disabled={isBulkResolving}
            onToggle={handleToggleOne}
            onToggleAll={handleToggleAll}
          />
          <Box className={styles.actions}>
            <Button
              small
              secondary
              error
              disabled={selectedCount === 0 || isBulkResolving}
              isLoading={bulkMode === 'reject'}
              onClick={handleBulkReject}
            >
              {`Reject selected (${selectedCount})`}
            </Button>
            <Button
              small
              disabled={selectedCount === 0 || isBulkResolving}
              isLoading={bulkMode === 'approve'}
              onClick={handleBulkApprove}
            >
              {`Approve selected (${selectedCount})`}
            </Button>
          </Box>
        </>
      )}

      {!isSelecting && approval && (
        <ApprovalCard
          key={approval.id}
          approval={approval}
          isApproving={resolvingState === ApprovalResolvingStatus.Approving}
          isRejecting={resolvingState === ApprovalResolvingStatus.Rejecting}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </Box>
  )
}

export default PendingApprovals
